<template>
  <div class="dashboard">
    <div class="invalid" v-if="!loading && !validOwnershipType">
      <EmptyDisplay title="Ownership type is not defined"
                    text="Please choose either Public or Private ownership to continue.">
        <RouterLink :to="`/account/${account.accountID}/edit`" custom v-slot="{ navigate }">
          <AppButton primary class="mt-4" @click="navigate">Fix Account</AppButton>
        </RouterLink>
      </EmptyDisplay>
    </div>
    <template v-else-if="!loading && validOwnershipType">
      <DOBenchmarking v-if="selected === 'dno'" :account="account" @needs-update="refreshData">
        <template #filterbar>
          <BenchmarkSelector :benchmark-options="benchmarkOptions" :selected="selected" @change="benchmarkSelectorChange" />
        </template>
      </DOBenchmarking>

      <CyberBenchmarking v-if="selected === 'cyber'" :account="account" @needs-update="refreshData">
        <template #filterbar>
          <BenchmarkSelector :benchmark-options="benchmarkOptions" :selected="selected" @change="benchmarkSelectorChange" />
        </template>
      </CyberBenchmarking>

      <FiduciaryBenchmarking v-if="selected === 'fiduciary'" :account="account" @needs-update="refreshData">
        <template #filterbar>
          <BenchmarkSelector :benchmark-options="benchmarkOptions" :selected="selected" @change="benchmarkSelectorChange" />
        </template>
      </FiduciaryBenchmarking>

      <UmbrellaBenchmarking v-if="selected === 'umbrella'">
        <template #filterbar>
          <BenchmarkSelector :benchmark-options="benchmarkOptions" :selected="selected" @change="benchmarkSelectorChange" />
        </template>
      </UmbrellaBenchmarking>

      <EPLBenchmarking v-if="selected === 'epl'" :account="account" @needs-update="refreshData">
        <template #filterbar>
          <BenchmarkSelector :benchmark-options="benchmarkOptions" :selected="selected" @change="benchmarkSelectorChange" />
        </template>
      </EPLBenchmarking>

      <CrimeBenchmarking v-if="selected === 'crime'" :account="account" @needs-update="refreshData">
        <template #filterbar>
          <BenchmarkSelector :benchmark-options="benchmarkOptions" :selected="selected" @change="benchmarkSelectorChange" />
        </template>
      </CrimeBenchmarking>

      <div v-if="!selected" class="m-auto flex flex-col items-center mt-8">

        <div class="flex flex-col items-center">
          <h2>Account Benchmarks</h2>
          <div>Please choose from one of the benchmarks below</div>
        </div>

        <div class="flex gap-4 mt-8 w-120 flex-wrap justify-center">
          <template v-for="option in benchmarkOptions" v-bind:key="option.name">
            <div v-if="userMeetsBenchmarkRequirements(option)" @click="loadBenchmark(option)">
              <div class="group tooltip pointer-events-auto z-20 cursor-pointer">
                <div class="flex h-24 w-36 flex-col justify-center rounded-lg border border-solid border-blue-200 bg-white shadow-md transition-all duration-150 group-hover:border-blue-200 group-hover:bg-blue-50 group-hover:shadow-none">
                  <div class="flex flex-col items-center gap-2 self-center p-2 text-black-500">
                    <Network v-if="option.name === 'D&O'" class="text-gray-400" />
                    <Users v-if="option.name === 'EPL'" class="text-gray-400" />
                    <Umbrella v-if="option.name === 'Umbrella'" class="text-gray-400" />
                    <CloudLightning v-if="option.name === 'Cyber'" class="text-gray-400" />
                    <BookCheck v-if="option.name === 'Fiduciary'" class="text-gray-400" />
                    <ShieldAlert v-if="option.name === 'Crime'" class="text-gray-400" />

                    <h2 class="text-center text-sm font-medium"> {{ option.name }}</h2>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </template>

    <PageSpinner v-else/>
  </div>
</template>

<script lang="ts">
import { Account } from '@/types/Account';
import { PropType } from 'vue';
import Store from '@/store';
import DOBenchmarking from './DOBenchmarking.vue';
import CyberBenchmarking from './CyberBenchmarking.vue';
import FiduciaryBenchmarking from './FiduciaryBenchmarking.vue';
import { referenceService } from '@/services/api';
import { BenchmarkContext, BenchmarkOption, benchmarkOptions } from './Benchmarking';
import EmptyDisplay from '@/components/EmptyDisplay.vue';
import benchmarkImage from '@/assets/images/benchmarking.svg';
import BenchmarkSelector from '@/views/account/benchmarking/BenchmarkSelector.vue';
import UmbrellaBenchmarking from '@/views/account/benchmarking/UmbrellaBenchmarking.vue';
import PageSpinner from '@/components/v2/PageSpinner.vue';
import EPLBenchmarking from '@/views/account/benchmarking/EPLBenchmarking.vue';
import CrimeBenchmarking from '@/views/account/benchmarking/CrimeBenchmarking.vue';
import { Umbrella, Users, Network, CloudLightning, BookCheck, ShieldAlert } from '@lucide/vue';
import AppButton from '@/components/v2/AppButton.vue';

export default {
  name: 'AccountBenchmarking',
  components: {
    AppButton,
    CrimeBenchmarking,
    EPLBenchmarking,
    Network, Users, Umbrella, CloudLightning, BookCheck, ShieldAlert,
    UmbrellaBenchmarking, BenchmarkSelector, EmptyDisplay, DOBenchmarking, CyberBenchmarking, FiduciaryBenchmarking, PageSpinner },
  props: {
    account: { required: true, type: Object as PropType<Account> },
    selected: { required: false, type: String as PropType<string> },
  },
  data() {
    return {
      loading: false,
      validOwnershipType: true,
      benchmarkImage,
      benchmarkOptions,
      context: {
        injectedExposureData: [],
        injectedExposureTypes: [],
        injectedExposureCategories: {}
      } as BenchmarkContext,
    };
  },
  provide() {
    return {
      context: this.context
    };
  },
  methods: {
    benchmarkSelectorChange(newSelected: string | undefined) {
      if (!newSelected || newSelected === this.selected) {
        return;
      }
      this.$router.push(newSelected);
    },
    async refreshData(): Promise<void> {
      this.loading = true;
      const ownershipType = this.account.ownershipType?.toLowerCase();
      if (!ownershipType) {
        this.validOwnershipType = false;
        this.loading = false;
        return;
      }
      (await referenceService.exposureCategories()).filter(c => {
        const scope = c.companyScope?.toLowerCase();
        return scope === 'all' ? true : scope === ownershipType;
      }).forEach(x => this.context.injectedExposureCategories[x.label] = x.value);
      this.context.injectedExposureTypes = (await referenceService.exposureTypes()).sort((m1, m2) => m1.name.localeCompare(m2.name));
      this.context.injectedExposureData = [];
      this.loading = false;
    },
    userMeetsBenchmarkRequirements(benchmark: BenchmarkOption): boolean {
      return benchmark.requirements.every(x => x(Store.state.user));
    },
    loadBenchmark(benchmark: BenchmarkOption) {
      this.$router.push(`benchmarking/${benchmark.link}`);
    },
    async initialize() {
      await this.refreshData();
    }
  },
  async mounted() {
    await this.initialize();
  },
  watch: {
    $route() {
      this.initialize();
    }
  }
};
</script>

<style scoped>
.actions {
  margin-top: 2rem;
  display: flex;
  flex-direction: row;
  grid-column-gap: 1rem;
}
</style>
